import React, { useCallback, useEffect, useRef, useState } from 'react';

// Declare custom element for TypeScript
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'video-player-container': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}
import {
  ParticipantPropertiesPayload,
  VideoClient,
  VideoPlayer,
} from '@zoom/videosdk';
import {
  StyleProp,
  ViewStyle,
  Image,
  Pressable,
  ImageStyle,
} from 'react-native';
import { useZoom } from '@/services/zoom/ZoomProvider';
import useStore from '@/stores/useStore';
import { useShallow } from 'zustand/react/shallow';
import styled from '@emotion/styled';
import Button from '@/shared/components/Button';
import SentryService from '@/services/sentry/Sentry.service';
import {
  green,
  accent,
  red,
  videoCellBackground,
  lightGreen,
  white,
  sand,
  black,
} from '@/shared/styles/colors';
import { LOG_VIDEO_STATS } from '@/shared/constants/flags';
import { VideoStatisticPayload } from '@/shared/types/video';
import { FONT_STYLES, ZIndex } from '@/shared/styles/constants';
import { RelativePathString, useRouter } from 'expo-router';
import { useSessionTimer } from '@/shared/hooks/useSessionTimer';
import { useVideoHeartbeat } from '@/shared/hooks/useVideoHeartbeat';
import { ROUTES } from '@/shared/constants/routes';
import EndCallModal from '@/shared/components/EndCallModal';

// Custom resolution map due to SDK import issue
const VIDEO_QUALITY = {
  Video_90P: 0,
  Video_180P: 1,
  Video_360P: 2,
  Video_720P: 3,
  Video_1080P: 4,
};

type ZoomClient = typeof VideoClient;

interface WebVideoUser {
  userId?: number;
  videoElement?: VideoPlayer | null;
  isVideoOn?: boolean;
  isMuted?: boolean;
  displayName?: string;
}

const ZoomVideoFeed = () => {
  const { zoomClient, endSession, sessionStartTime } = useZoom();

  const applyVideoStyles = (
    videoPlayer: VideoPlayer,
    containerRef: React.RefObject<HTMLDivElement>,
  ) => {
    videoPlayer.style.height = '100%';
    videoPlayer.style.width = '100%';
    videoPlayer.style.objectFit = 'cover';

    // Wait for the actual <video> element to be created and style it
    const waitForVideoElement = () => {
      const videoElement = containerRef.current?.querySelector('video');

      if (videoElement) {
        videoElement.style.objectFit = 'cover';
        return;
      }
    };

    setTimeout(() => {
      waitForVideoElement();
    }, 100);
  };
  const { formattedTime } = useSessionTimer(sessionStartTime);

  const {
    setIsInSession,
    activeSessionName,
    setActiveSessionName,
    otherUserDisconnected,
    setOtherUserDisconnected,
    isStylist,
  } = useStore(
    useShallow((state) => ({
      setIsInSession: state.setIsInSession,
      activeSessionName: state.activeSessionName,
      setActiveSessionName: state.setActiveSessionName,
      otherUserDisconnected: state.otherUserDisconnected,
      setOtherUserDisconnected: state.setOtherUserDisconnected,
      isStylist: state.isStylist,
    })),
  );
  const router = useRouter();

  // Set correct web zoom client type
  const client: ZoomClient = zoomClient as unknown as ZoomClient;

  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);

  const [currentUser, setCurrentUser] = useState<WebVideoUser>();
  const [remoteUser, setRemoteUser] = useState<WebVideoUser>();
  const [isEndCallModalVisible, setIsEndCallModalVisible] = useState(false);

  const currentUserContainerRef = useRef<HTMLDivElement | null>(null);
  const remoteUserContainerRef = useRef<HTMLDivElement | null>(null);

  // Start video session heartbeat
  useVideoHeartbeat({
    sessionName: activeSessionName,
    isActive: true,
  });

  useEffect(() => {
    const startVideo = async () => {
      try {
        const stream = client.getMediaStream();

        if (!stream) {
          throw new Error('Start self video: Failed to get MediaStream');
        }

        const startVideoWithRetry = async (maxRetries = 5, delay = 1000) => {
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              await stream.startVideo({
                hd: true,
              });
              return;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
              // If video is already started, just exit and continue
              if (
                error?.type === 'INVALID_OPERATION' &&
                error?.reason === 'Video is started'
              ) {
                return;
              }

              // If the camera is still initializing, wait and retry
              if (
                error?.type === 'INVALID_OPERATION' &&
                error?.reason === 'Camera is starting,please wait.' &&
                attempt < maxRetries
              ) {
                await new Promise((resolve) => setTimeout(resolve, delay * 2));
                continue;
              }

              throw error;
            }
          }
        };

        await startVideoWithRetry();
        await stream.startAudio();

        // Log current user stream info
        LOG_VIDEO_STATS &&
          console.log('stream supports HD video:', stream.isSupportHDVideo());
        LOG_VIDEO_STATS &&
          console.log(
            'Current user max video quality:',
            stream.getVideoMaxQuality(),
          );
        LOG_VIDEO_STATS &&
          console.log(
            'SharedArrayBuffer supported:',
            typeof SharedArrayBuffer !== 'undefined',
          );

        // Attach video to current user container
        const user = client.getCurrentUserInfo();

        const videoPlayer = await stream.attachVideo(
          user.userId,
          VIDEO_QUALITY.Video_720P,
        );

        applyVideoStyles(videoPlayer as VideoPlayer, currentUserContainerRef);

        setCurrentUser({
          userId: user.userId,
          videoElement: videoPlayer as VideoPlayer,
          displayName: user.displayName,
        });
      } catch (error) {
        SentryService.captureError('Failed to render self video', error);
      }
    };

    startVideo();
  }, [client]);

  const renderRemoteVideo = useCallback(async () => {
    try {
      const stream = client.getMediaStream();

      LOG_VIDEO_STATS &&
        console.log('renderRemoteVideo - remoteUser:', remoteUser);

      if (!stream) {
        throw new Error('Start remote user video: Failed to get stream');
      }

      if (!remoteUser?.userId) {
        throw new Error('Remote user not found');
      }

      const videoContainer = remoteUserContainerRef.current;

      if (!videoContainer) {
        throw new Error(
          'Render remote video: Video player container not found',
        );
      }

      // Remove any existing video element
      videoContainer.innerHTML = '';

      const videoPlayer = await stream.attachVideo(
        remoteUser.userId,
        VIDEO_QUALITY.Video_720P,
      );

      if (
        videoPlayer &&
        typeof videoPlayer === 'object' &&
        'style' in videoPlayer
      ) {
        videoPlayer.style.width = '100%';
        videoPlayer.style.height = '100%';
        videoPlayer.style.objectFit = 'cover';
      }

      setRemoteUser((prev) => ({
        ...prev,
        displayName: remoteUser.displayName,
        userId: remoteUser.userId,
        videoElement: videoPlayer as VideoPlayer,
      }));
    } catch (error) {
      SentryService.captureError('Failed to render participant video', error);
    }
  }, [client, remoteUser]);

  // Show existing remote user video on join
  useEffect(() => {
    if (!client) {
      return;
    }

    const allUsers = client.getAllUser();

    if (!allUsers.length) {
      return;
    }

    for (const user of allUsers) {
      if (user.userId !== client.getCurrentUserInfo().userId) {
        setRemoteUser({
          userId: user.userId,
          videoElement: null,
          isVideoOn: user.bVideoOn,
          isMuted: user.muted,
          displayName: user.displayName,
        });
      }
    }
  }, [client]);

  // Render remote user video when ready
  useEffect(() => {
    if (!remoteUser?.videoElement && remoteUser?.isVideoOn) {
      renderRemoteVideo();
    }
  }, [remoteUser?.isVideoOn, remoteUser?.videoElement, renderRemoteVideo]);

  // Show current user video
  useEffect(() => {
    const container = currentUserContainerRef.current;
    if (!container) {
      return;
    }

    if (
      currentUser?.videoElement &&
      !container.contains(currentUser.videoElement)
    ) {
      container.innerHTML = '';
      container.appendChild(currentUser.videoElement);
    }
  }, [currentUser?.videoElement]);

  // Show remote user video
  useEffect(() => {
    const container = remoteUserContainerRef.current;
    if (!container) {
      return;
    }

    if (
      remoteUser?.videoElement &&
      !container.contains(remoteUser.videoElement)
    ) {
      container.innerHTML = '';
      container.appendChild(remoteUser.videoElement);
    }
  }, [remoteUser?.videoElement]);

  // Define event listeners
  const onUserAdded = useCallback(
    (payload: ParticipantPropertiesPayload[]) => {
      __DEV__ && console.log('User added', payload);

      setRemoteUser({
        userId: payload[0].userId,
        displayName: payload[0].displayName,
        videoElement: null,
        isVideoOn: payload[0].bVideoOn,
        isMuted: payload[0].muted,
      });
    },
    [setRemoteUser],
  );

  const onUserUpdated = useCallback(
    (payload: ParticipantPropertiesPayload[]) => {
      __DEV__ && console.log('User updated', payload);

      const userId = payload[0].userId;

      if (userId === remoteUser?.userId) {
        setRemoteUser((prev) => {
          if (!prev) return prev;

          // Check if any properties actually changed before creating new object
          const hasVideoChange =
            Object.prototype.hasOwnProperty.call(payload[0], 'bVideoOn') &&
            prev.isVideoOn !== payload[0].bVideoOn;
          const hasAudioChange =
            Object.prototype.hasOwnProperty.call(payload[0], 'muted') &&
            prev.isMuted !== payload[0].muted;

          // Only create new object if something actually changed
          if (!hasVideoChange && !hasAudioChange) {
            return prev;
          }

          const newState: WebVideoUser = {
            userId: prev.userId,
            videoElement: prev.videoElement,
            displayName: prev.displayName,
            isVideoOn: hasVideoChange ? payload[0].bVideoOn : prev.isVideoOn,
            isMuted: hasAudioChange ? payload[0].muted : prev.isMuted,
          };

          return newState;
        });
      }
    },
    [remoteUser?.userId], // Only depend on userId, not entire remoteUser object
  );

  const onUserRemoved = useCallback(
    (payload: ParticipantPropertiesPayload[]) => {
      const removedUserId = payload[0].userId;
      if (removedUserId === remoteUser?.userId) {
        // Clean up video element
        const container = remoteUserContainerRef.current;
        if (container) {
          container.innerHTML = '';
        }
        setRemoteUser(undefined);
      }
    },
    [remoteUser?.userId], // Only depend on userId, not entire remoteUser object
  );

  // Listen for video quality data
  const onVideoStatisticChange = useCallback(
    (payload: VideoStatisticPayload) => {
      console.log('Video statistic state change', payload);
    },
    [],
  );

  useEffect(() => {
    if (LOG_VIDEO_STATS) {
      const stream = client.getMediaStream();
      stream.subscribeVideoStatisticData({ encode: true, decode: true });
    }
  }, [client]);

  // Attach event listeners
  useEffect(() => {
    if (!client) {
      return;
    }

    client.on('user-added', onUserAdded);
    client.on('user-updated', onUserUpdated);
    client.on('user-removed', onUserRemoved);
    client.on('video-statistic-data-change', onVideoStatisticChange);

    return () => {
      client.off('user-added', onUserAdded);
      client.off('user-updated', onUserUpdated);
      client.off('user-removed', onUserRemoved);
      client.off('video-statistic-data-change', onVideoStatisticChange);
    };
  }, [
    client,
    onUserAdded,
    onUserRemoved,
    onUserUpdated,
    onVideoStatisticChange,
  ]);

  // User controls
  const toggleAudio = async () => {
    const stream = client.getMediaStream();
    if (!stream) return;

    if (isAudioMuted) {
      await stream.unmuteAudio();
    } else {
      await stream.muteAudio();
    }
    setIsAudioMuted(!isAudioMuted);
  };

  const toggleVideo = async () => {
    const stream = client.getMediaStream();
    if (!stream) return;

    if (isVideoMuted) {
      // Re-start video and re-attach video element
      await stream.startVideo();

      // Re-attach video to current user container
      const user = client.getCurrentUserInfo();
      const videoPlayer = await stream.attachVideo(
        user.userId,
        VIDEO_QUALITY.Video_720P,
      );

      applyVideoStyles(videoPlayer as VideoPlayer, currentUserContainerRef);

      setCurrentUser((prev) => ({
        ...prev,
        userId: user.userId,
        videoElement: videoPlayer as VideoPlayer,
        displayName: user.displayName,
      }));
    } else {
      await stream.stopVideo();

      // Clean up video element when stopping
      const container = currentUserContainerRef.current;
      if (container) {
        container.innerHTML = '';
      }

      setCurrentUser((prev) => ({
        ...prev,
        videoElement: null,
      }));
    }
    setIsVideoMuted(!isVideoMuted);
  };

  const leaveSession = useCallback(async () => {
    try {
      await endSession();
      // Navigate back to sessions with post-call modal
      router.replace(
        `${ROUTES.SESSIONS.path}?showPostCall=true` as RelativePathString,
      );
    } catch (error) {
      // Leave the session even if endSession failed
      await client.leave();
      SentryService.captureError('Error leaving web video session', error);
      // Still navigate back on error
      router.replace(ROUTES.SESSIONS.path);
    } finally {
      setIsInSession(false);
      setActiveSessionName('');
    }
  }, [client, endSession, router, setActiveSessionName, setIsInSession]);

  const exitCallOnly = useCallback(async () => {
    try {
      // Only leave the Zoom session, don't end the backend session
      await client.leave();
      // Navigate back to sessions without post-call modal
      router.replace(ROUTES.SESSIONS.path);
    } catch (error) {
      SentryService.captureError('Error exiting web video call', error);
      router.replace(ROUTES.SESSIONS.path);
    } finally {
      setIsInSession(false);
      setActiveSessionName('');
    }
  }, [client, router, setActiveSessionName, setIsInSession]);

  const handleEndCallPress = useCallback(() => {
    // Show modal for both stylists and clients to choose between ending session or just exiting call
    setIsEndCallModalVisible(true);
  }, []);

  const handleEndSessionAndExit = useCallback(async () => {
    setIsEndCallModalVisible(false);
    await leaveSession();
  }, [leaveSession]);

  const handleExitCallOnly = useCallback(async () => {
    setIsEndCallModalVisible(false);
    await exitCallOnly();
  }, [exitCallOnly]);

  // Handle other user disconnection
  useEffect(() => {
    if (otherUserDisconnected) {
      setOtherUserDisconnected(false);
      leaveSession();
    }
  }, [otherUserDisconnected, setOtherUserDisconnected, leaveSession]);

  return (
    <OuterContainer>
      <Header>
        <Image
          source={require('@/assets/images/EmmeLogo.png')}
          alt="Emme Logo"
          resizeMode="contain"
          style={{ width: 104, height: 34 }}
        />
        <Button
          text="End Call"
          onPress={handleEndCallPress}
          customStyle={endCallButtonStyles}
          customLabelStyle={{ fontSize: 13 }}
          size="small"
          compact={true}
        />
      </Header>
      <InnerContainer>
        <VideoLayout>
          <VideoWrapper>
            <video-player-container
              id="self-container"
              className="video-player-container"
              ref={currentUserContainerRef}
              style={videoContainerStyles}
            />
            <NameLabel>
              {isAudioMuted && (
                <Image
                  source={require('@/assets/images/video/MutedIcon.png')}
                  alt="muted"
                  resizeMode="contain"
                  style={{ width: 11, height: 11, marginRight: 5 }}
                />
              )}
              You
            </NameLabel>
            {isVideoMuted && (
              <VideoOverlay>
                <Image
                  source={require('@/assets/images/video/EmptyViewIcon.png')}
                  alt="Video Off"
                  resizeMode="contain"
                  style={{ width: 76, height: 76 }}
                />
              </VideoOverlay>
            )}
          </VideoWrapper>
          <VideoWrapper>
            <video-player-container
              id="remote-container"
              className="video-player-container"
              ref={remoteUserContainerRef}
              style={videoContainerStyles}
            />
            {remoteUser?.displayName && (
              <NameLabel>
                {remoteUser?.isMuted && (
                  <Image
                    source={require('@/assets/images/video/MutedIcon.png')}
                    alt="muted"
                    resizeMode="contain"
                    style={{ width: 11, height: 11, marginRight: 5 }}
                  />
                )}
                {remoteUser?.displayName}
              </NameLabel>
            )}
            {!remoteUser?.isVideoOn && (
              <VideoOverlay>
                <Image
                  source={require('@/assets/images/video/EmptyViewIcon.png')}
                  alt="Video Off"
                  resizeMode="contain"
                  style={{ width: 76, height: 76 }}
                />
              </VideoOverlay>
            )}
          </VideoWrapper>
        </VideoLayout>

        <ControlsRow>
          <TimerWrapper>
            <DurationTimer>{formattedTime}</DurationTimer>
          </TimerWrapper>
          <ButtonRow>
            <Pressable onPress={toggleAudio}>
              {isAudioMuted ? (
                <Image
                  style={controlImageStyles}
                  source={require('@/assets/images/video/AudioOffDesktopIcon.png')}
                  resizeMode="contain"
                />
              ) : (
                <Image
                  style={controlImageStyles}
                  source={require('@/assets/images/video/AudioOnIcon.png')}
                  resizeMode="contain"
                />
              )}
            </Pressable>
            <Pressable onPress={toggleVideo}>
              {isVideoMuted ? (
                <Image
                  style={controlImageStyles}
                  source={require('@/assets/images/video/VideoOffDesktopIcon.png')}
                  resizeMode="contain"
                />
              ) : (
                <Image
                  style={controlImageStyles}
                  source={require('@/assets/images/video/VideoOnIcon.png')}
                  resizeMode="contain"
                />
              )}
            </Pressable>
          </ButtonRow>
        </ControlsRow>
      </InnerContainer>
      <EndCallModal
        isVisible={isEndCallModalVisible}
        setIsVisible={setIsEndCallModalVisible}
        onEndSessionAndExit={handleEndSessionAndExit}
        onExitCallOnly={handleExitCallOnly}
        isStylist={isStylist ?? false}
      />
    </OuterContainer>
  );
};

export default ZoomVideoFeed;

const OuterContainer = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: green,
  alignItems: 'center',
  maxWidth: '100vw',
});

const InnerContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  justifyContent: 'center',
});

const Header = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '26px 44px 18.5px',
  borderBottom: `1px solid ${accent}`,
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
});

const VideoLayout = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 7,
});

const VideoWrapper = styled.div({
  position: 'relative',
  width: 640,
  height: 360,
});

const VideoOverlay = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: videoCellBackground,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: ZIndex.BottomLayer,
});

const NameLabel = styled.div({
  position: 'absolute',
  bottom: 0,
  left: 0,
  color: white,
  fontSize: 11.5,
  padding: 5,
  minWidth: 61,
  backgroundColor: lightGreen,
  zIndex: ZIndex.MiddleLayer,
  display: 'flex',
  ...FONT_STYLES.helvetica,
});

const ControlsRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: 52,
  width: '100%',
  position: 'relative',
});

const TimerWrapper = styled.div({
  position: 'absolute',
  left: 0,
  display: 'flex',
  justifyContent: 'flex-start',
  paddingLeft: 'calc((100vw - 1287px) / 2)', // 640*2 + 7px gap
  width: '100%',
});

const DurationTimer = styled.div({
  color: sand,
  fontSize: 20,
  letterSpacing: -0.2,
  ...FONT_STYLES.lora,
  marginLeft: 24,
});

const ButtonRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 12.5,
  width: '100%',
});

const videoContainerStyles: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: black,
  overflow: 'hidden',
};

const controlImageStyles: StyleProp<ImageStyle> = {
  width: 55,
  height: 55,
};

const endCallButtonStyles: StyleProp<ViewStyle> = {
  backgroundColor: red,
  width: 100,
  padding: 0,
};
